import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Box, Button, Card, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ConfirmDelete from '../../../shared/components/ConfirmDelete';
import CustomDataGrid from '../../../shared/components/CustomDataGrid';
import { ERole } from '../../../shared/enums/user/ERole';
import { handleError } from '../../../shared/functions/handleError';
import { usePagination } from '../../../shared/hooks/usePagination';
import { useProjectColumns } from '../../../shared/hooks/useProjectColumns';
import { IProject } from '../../../shared/interfaces/projects/IProject';
import { RootState } from '../../../store/store';
import AdminClients from '../clients';
import { AdminProjectsService } from './service';

const AdminProjects = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  const [data, setData] = useState<IProject[] | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<undefined | IProject>(
    undefined,
  );
  const columns = useProjectColumns(setSelectedProject, setShowDeleteModal);
  const {
    pagination,
    count,
    setPagination,
    totalPages,
    setTotalPages,
    handleUpdateList,
    setCount,
    changeFilterHandler,
  } = usePagination({
    perPage: 15,
  });

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedProject(undefined);
  };
  const deleteHandler = (id?: number) => {
    !!id &&
      AdminProjectsService.delete(id)
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(
              t('toasts.successfullyDeleted', { title: selectedProject?.name }),
              {
                variant: 'success',
              },
            );
            closeDeleteModal();
          }
        })
        .catch((err) => handleError(err));
  };

  useEffect(() => {
    AdminProjectsService.get({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setCount(response?.count);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch((err) => handleError(err));
  }, [pagination, setTotalPages, setCount]);

  return (
    <>
      <Card>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: '20px',
          }}
        >
          <TextField
            onChange={(e) => {
              changeFilterHandler('name', e.target.value);
            }}
            label={t('general.searchByName')}
            sx={{
              width: { xs: '100%', md: 'auto' },
              paddingBottom: { xs: 2, md: 0 },
            }}
          />

          {!isNormalUser && (
            <Button
              variant={'contained'}
              onClick={() => navigate('/admin/projects/create')}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <AddCircleOutlineOutlinedIcon sx={{ marginRight: '5px' }} />
              {t('button.addNewProject')}
            </Button>
          )}
        </Box>
        <CustomDataGrid
          columns={columns}
          rows={data || []}
          rowCount={count}
          setPagination={setPagination}
          pagination={pagination}
          loading={data === undefined}
          hasNextPage={totalPages < pagination?.page}
        />

        <ConfirmDelete
          open={showDeleteModal}
          title={selectedProject?.name}
          handleClose={closeDeleteModal}
          handleSubmit={() => deleteHandler(selectedProject?.id)}
        />
      </Card>
      {userData?.role !== ERole.USER && <AdminClients />}
    </>
  );
};

export default AdminProjects;
